import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Check, Star, Shield, Heart, Trophy, Users, Clock, Gift, TrendingUp, Zap } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AgeVerificationModal from '@/components/AgeVerificationModal';
import LotteryEntryForm from '@/components/LotteryEntryForm';
import NotificationBanner from '@/components/NotificationBanner';
import LiveResults from '@/components/LiveResults';
import NumberAnalytics from '@/components/NumberAnalytics';
import LiveChat from '@/components/LiveChat';

const Index = () => {
  const [showAgeModal, setShowAgeModal] = useState(false);

  useEffect(() => {
    // Check age verification
    if (!localStorage.getItem('ageVerified')) {
      setShowAgeModal(true);
    }
  }, []);

  const handleAgeVerification = (isOver18: boolean) => {
    if (isOver18) {
      localStorage.setItem('ageVerified', 'true');
      setShowAgeModal(false);
    } else {
      window.location.href = 'https://gambleaware.nsw.gov.au';
    }
  };

  const testimonials = [
    {
      name: "Sarah M.",
      location: "Sydney, NSW",
      quote: "I never thought I'd win, but GoldenTicketLucky changed my life! The $25,000 prize helped me pay off my mortgage.",
      amount: "$25,000",
      game: "PowerBall"
    },
    {
      name: "Michael R.",
      location: "Melbourne, VIC",
      quote: "Amazing experience! The team was so helpful when I won. Professional service from start to finish.",
      amount: "$18,500",
      game: "Oz Lotto"
    },
    {
      name: "Emma T.",
      location: "Brisbane, QLD",
      quote: "Fair play, great odds, and fantastic customer service. I've been playing for 2 years now.",
      amount: "$50,000",
      game: "Saturday Lotto"
    }
  ];

  const lotteryStats = [
    {
      icon: <Trophy className="w-8 h-8 text-yellow-600" />,
      value: "$127M+",
      label: "Total Prizes Awarded"
    },
    {
      icon: <Users className="w-8 h-8 text-yellow-600" />,
      value: "245K+",
      label: "Happy Winners"
    },
    {
      icon: <Star className="w-8 h-8 text-yellow-600" />,
      value: "4.9/5",
      label: "Customer Rating"
    },
    {
      icon: <Clock className="w-8 h-8 text-yellow-600" />,
      value: "24/7",
      label: "Support Available"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <AgeVerificationModal
        showModal={showAgeModal}
        onVerification={handleAgeVerification}
      />

      <NotificationBanner />

      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-yellow-500 to-yellow-300 py-20 relative bg-cover bg-center py-20" style={{ backgroundImage: 'url("/images/image.webp")' }}>
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 relative z-10">
            Grab Your GoldenTicketLucky to Millions!
          </h1>
          <p className="text-xl text-white mb-8 max-w-2xl mx-auto relative z-10">
            Australia's trusted lottery with big dreams and bigger wins. Join thousands of Aussies chasing their golden opportunity.
          </p>
          <Button
            className="bg-yellow-600 hover:bg-yellow-700 text-white px-8 py-4 text-lg rounded-md transition-all transform hover:scale-105 relative z-10"
            onClick={() => document.getElementById('entry-form')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Play Now
          </Button>
          <div className="mt-8 flex justify-center">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center animate-pulse">
              <Star className="w-8 h-8 text-yellow-600" />
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {lotteryStats.map((stat, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-3">
                    {stat.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{stat.value}</h3>
                  <p className="text-gray-600">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <LiveResults />

      <LotteryEntryForm />

      <NumberAnalytics />

      <LiveChat />

      {/* How It Works Section */}
      <section className="py-16 bg-yellow-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Choose Your Numbers</h3>
              <p className="text-gray-700">Select your lucky numbers or use Quick Pick for random selection. Choose from PowerBall, Oz Lotto, or Saturday Lotto.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Place Your Bet</h3>
              <p className="text-gray-700">Secure payment processing with multiple options. Play from $5 up to premium VIP entries for bigger chances.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Win Big</h3>
              <p className="text-gray-700">Check results live, get instant notifications, and claim your winnings. We handle everything securely and professionally.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why GoldenTicketLucky Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Why Choose GoldenTicketLucky?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <Shield className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-4">Trusted & Secure</h3>
                <p className="text-gray-700">Your data is safe with us. We use industry-leading security measures to protect your information.</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <Heart className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-4">Supports Aussie Charities</h3>
                <p className="text-gray-700">Every ticket helps support local Australian charities and community causes across the nation.</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <Check className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-4">Play Responsibly</h3>
                <p className="text-gray-700">We promote responsible gambling with tools and resources to help you play within your limits.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Winner Testimonials */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">What Our Winners Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <Trophy className="w-6 h-6 text-yellow-600 mr-2" />
                    <span className="font-bold text-yellow-600">{testimonial.amount}</span>
                    <span className="text-gray-600 ml-2">• {testimonial.game}</span>
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.quote}"</p>
                  <div className="border-t pt-4">
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-gray-600 text-sm">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Wins Section */}
      <section className="py-16 bg-yellow-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Recent Winners</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="bg-white">
              <CardContent className="p-6 text-center">
                <Trophy className="w-8 h-8 text-yellow-600 mx-auto mb-3" />
                <p className="font-bold text-gray-900">Sarah M. from NSW</p>
                <p className="text-yellow-600 text-xl font-bold">$25,000</p>
                <p className="text-gray-600 text-sm">Won 3 days ago</p>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardContent className="p-6 text-center">
                <Trophy className="w-8 h-8 text-yellow-600 mx-auto mb-3" />
                <p className="font-bold text-gray-900">John D. from VIC</p>
                <p className="text-yellow-600 text-xl font-bold">$15,000</p>
                <p className="text-gray-600 text-sm">Won 1 week ago</p>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardContent className="p-6 text-center">
                <Trophy className="w-8 h-8 text-yellow-600 mx-auto mb-3" />
                <p className="font-bold text-gray-900">Emma T. from QLD</p>
                <p className="text-yellow-600 text-xl font-bold">$50,000</p>
                <p className="text-gray-600 text-sm">Won 2 weeks ago</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Special Features Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Premium Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <Zap className="w-10 h-10 text-yellow-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-2">Instant Results</h3>
                <p className="text-gray-600 text-sm">Get notified immediately when draws happen</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <Gift className="w-10 h-10 text-yellow-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-2">Bonus Entries</h3>
                <p className="text-gray-600 text-sm">Earn free tickets with regular play</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <TrendingUp className="w-10 h-10 text-yellow-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-2">Number Analytics</h3>
                <p className="text-gray-600 text-sm">View hot and cold number statistics</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <Shield className="w-10 h-10 text-yellow-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-2">Auto-Claim</h3>
                <p className="text-gray-600 text-sm">Winnings automatically credited to account</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-yellow-500 to-yellow-300">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Ready to Win Your Golden Opportunity?</h2>
          <p className="text-xl text-gray-700 mb-8">Join thousands of Australians who trust GoldenTicketLucky with their dreams</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-8 py-4 text-lg rounded-md transition-all transform hover:scale-105"
              onClick={() => window.location.href = '/play'}
            >
              Start Playing Now
            </Button>
            <Button
              variant="outline"
              className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-8 py-4 text-lg rounded-md transition-all"
              onClick={() => window.location.href = '/about'}
            >
              Learn More
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
